'use client';

/**
 * Cinema8 video player hero — 2-column layout with a 16:9 player on
 * the left and metadata on the right. Ported from ThemeMaker's
 * {@code blocks/hero/VideoPlayerHero}.
 *
 * <h3>Cinema8 mounting</h3>
 * The {@code <cinema8-player>} custom element mutates its DOM
 * synchronously after script load. React's hydration check fires a
 * hard error ("did not expect server HTML to contain a {@code <div>}
 * in {@code <cinema8-player>}") because the DOM the server sent
 * doesn't match what React tries to hydrate against — and once
 * hydration bails the subtree, ThemeTokens CSS variable injection
 * never finishes for that subtree, so dark themes render as light.
 *
 * <p>Mitigation: server renders an empty container; {@link useEffect}
 * injects {@code <cinema8-player>} as raw HTML via {@code innerHTML}
 * so React never tracks its descendants.
 *
 * <h3>Player script</h3>
 * The {@code <script async>} for {@code player.js} mounts at
 * {@code https://static-01.cinema8.com/embed/player.js}. Idempotent —
 * multiple emissions on a page are deduplicated by the browser via
 * URL match.
 */
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { tx } from '../_shared/coerce';
import { Button } from '@akademi/ui';
import { BlockRoot } from '../_shared/BlockRoot';

type Localized = string | Record<string, string>;

export interface VideoPlayerHeroProps {
  /** Cinema8 media id (pulled from {@code cinema8.com/video/<id>}). */
  mediaId?: string;
  /** Optional poster shown before the player upgrades. */
  poster?: string;
  /** Eyebrow above the title (e.g. "Episode 3 of 8"). */
  kicker?: Localized;
  title?: Localized;
  subtitle?: Localized;
  description?: Localized;
  /** Pre-formatted metadata strings (author / year / runtime / etc). */
  meta?: Localized[];
  /** Primary action — filled pill. */
  primaryActionLabel?: Localized;
  primaryActionHref?: string;
  /** Up to 3 secondary actions — outlined pills. */
  secondaryActions?: Array<{ label?: Localized; href?: string }>;
  /** Tag pills below the actions. */
  tags?: Localized[];
  /** {@code 'page'} (default) or {@code 'card'} for an elevated frame. */
  background?: 'page' | 'card';
  /** 'full' (default) lets content span the section edge-to-edge;
   *  'theme' constrains it to the page's max-width container. */
  maxWidth?: 'full' | 'theme';
}

export function VideoPlayerHero({
  mediaId = 'jXayvMX7',
  poster,
  kicker,
  title,
  subtitle,
  description,
  meta = [],
  primaryActionLabel,
  primaryActionHref,
  secondaryActions = [],
  tags = [],
  background = 'page',
  maxWidth = 'full',
}: VideoPlayerHeroProps) {
  const { t } = useTranslation('site-builder-blocks');
  const titleText = tx(title) || t('hero.videoPlayer.untitled');
  const bg =
    background === 'card'
      ? 'var(--color-surface-card, #fff)'
      : 'var(--color-surface-page, #fbf7ee)';

  const playerHostRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const host = playerHostRef.current;
    if (!host || !mediaId) return;
    const safeMediaId = mediaId.replace(/[<>"']/g, '');
    const safePoster = (poster ?? '').replace(/[<>"']/g, '');
    const posterAttr = safePoster ? ` poster="${safePoster}"` : '';
    host.innerHTML =
      `<cinema8-player media-id="${safeMediaId}"${posterAttr} ` +
      `style="position:absolute;inset:0;width:100%;height:100%;border:0"></cinema8-player>`;

    // player.js (loaded by <cinema8-player>) injects its own <iframe>
    // asynchronously and never gives it a title, which fails the
    // accessible-name audit. We don't control that script's markup, so
    // watch for the iframe and label it ourselves once it lands.
    const label = tx(title) || t('hero.videoPlayer.frameTitle');
    const applyTitle = () => {
      const frame = host.querySelector('iframe');
      if (frame && !frame.title) frame.title = label;
      return frame;
    };
    if (applyTitle()) return;
    const observer = new MutationObserver(() => {
      if (applyTitle()) observer.disconnect();
    });
    observer.observe(host, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, [mediaId, poster, title, t]);

  return (
    <BlockRoot
      as="section"
      style={{
        background: bg,
        padding: 'clamp(28px, 4vw, 56px) clamp(16px, 2.5vw, 24px)',
        ...(maxWidth === 'theme'
          ? {
              maxWidth: 'var(--sb-max-width-1200, var(--app-max-width, 1200px))',
              width: '100%',
              margin: '0 auto',
            }
          : {}),
      }}
    >
      <style
        dangerouslySetInnerHTML={{
          __html: `
        .lms-vph-grid {
          display: grid;
          grid-template-columns: minmax(0, 1.4fr) minmax(0, 1fr);
          gap: clamp(20px, 3vw, 36px);
          align-items: center;
          max-width: 1280px;
          margin: 0 auto;
        }
        @media (max-width: 880px) {
          .lms-vph-grid { grid-template-columns: 1fr; gap: 20px; }
          .lms-vph-meta { padding: 0 4px; }
        }
        .lms-vph-meta { min-width: 0; }
      `,
        }}
      />

      <div className="lms-vph-grid">
        {/* 16:9 player frame. Injected via innerHTML in the effect
         *  above, so this div has no React children and there's no
         *  hydration tree to mismatch. suppressHydrationWarning kept
         *  as belt-and-braces for browser extensions that mutate it. */}
        <div
          ref={playerHostRef}
          suppressHydrationWarning
          style={{
            position: 'relative',
            aspectRatio: '16 / 9',
            background: 'var(--color-neutral-950, #0a0a0a)',
            borderRadius: 'var(--radius-md, 6px)',
            overflow: 'hidden',
            boxShadow: 'var(--shadow-lg, 0 12px 32px rgba(0,0,0,0.14))',
            backgroundImage: poster ? `url("${poster}")` : undefined,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        />

        {/* Cinema8 player.js — idempotent script load. */}
        <script async src="https://static-01.cinema8.com/embed/player.js" />

        {/* Metadata column. Right of the player on desktop, below on
         *  mobile. Order: kicker → title → subtitle → meta row → long
         *  description → CTA row → tag pills. */}
        <div className="lms-vph-meta">
          {kicker && (
            <div
              style={{
                fontSize: '11px',
                fontWeight: 700,
                letterSpacing: 'var(--font-tracking-widest, 0.12em)',
                textTransform: 'uppercase',
                color: 'var(--color-brand-700, #722f37)',
                marginBottom: 12,
              }}
            >
              {tx(kicker)}
            </div>
          )}
          <h1
            style={{
              margin: 0,
              fontSize: 'clamp(28px, 3.4vw, 44px)',
              lineHeight: 1.1,
              fontWeight: 700,
              letterSpacing: 'var(--font-tracking-snug, -0.015em)',
              color: 'var(--color-text-primary, #1a1614)',
            }}
          >
            {titleText}
          </h1>

          {subtitle && (
            <p
              style={{
                marginTop: 8,
                marginBottom: 0,
                fontSize: 'clamp(15px, 1.2vw, 18px)',
                lineHeight: 1.45,
                color: 'var(--color-text-secondary, #5d4f47)',
              }}
            >
              {tx(subtitle)}
            </p>
          )}

          {meta.length > 0 && (
            <div
              style={{
                marginTop: 14,
                fontSize: 'var(--font-size-xs, 12px)',
                color: 'var(--color-text-tertiary, #94867d)',
                display: 'flex',
                flexWrap: 'wrap',
                columnGap: 14,
                rowGap: 6,
              }}
            >
              {meta.map((m, i) => (
                <span key={i}>{tx(m)}</span>
              ))}
            </div>
          )}

          {description && (
            <p
              style={{
                marginTop: 16,
                marginBottom: 0,
                fontSize: 'var(--font-size-sm, 14px)',
                lineHeight: 1.6,
                color: 'var(--color-text-secondary, #5d4f47)',
              }}
            >
              {tx(description)}
            </p>
          )}

          {(primaryActionLabel || secondaryActions.length > 0) && (
            <div
              style={{
                marginTop: 20,
                display: 'flex',
                flexWrap: 'wrap',
                gap: 10,
              }}
            >
              {primaryActionLabel && (
                <Button
                  href={primaryActionHref ?? '#'}
                  style={{
                    height: 'auto',
                    minHeight: 44, // WCAG 2.5.5 touch target
                    padding: 'var(--space-2-5, 10px) var(--space-5, 20px)',
                    background: 'var(--color-brand-500, #722f37)',
                  }}
                >
                  {tx(primaryActionLabel)}
                </Button>
              )}
              {secondaryActions.slice(0, 3).map((a, i) => (
                <Button
                  href={a.href ?? '#'}
                  key={i}
                  variant="outline"
                  style={{
                    height: 'auto',
                    minHeight: 44,
                    padding: 'var(--space-2-5, 10px) var(--space-5, 20px)',
                    fontSize: '13px',
                    fontWeight: 500,
                  }}
                >
                  {tx(a.label)}
                </Button>
              ))}
            </div>
          )}

          {tags.length > 0 && (
            <div
              style={{
                marginTop: 16,
                display: 'flex',
                flexWrap: 'wrap',
                gap: 6,
              }}
            >
              {tags.map((tg, i) => (
                <span
                  key={i}
                  style={{
                    fontSize: '11px',
                    padding: 'var(--space-1, 4px) var(--space-2-5, 10px)',
                    background: 'var(--color-surface-page, rgba(0,0,0,0.04))',
                    color: 'var(--color-text-secondary, #5d4f47)',
                    borderRadius: 999,
                  }}
                >
                  {tx(tg)}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>
    </BlockRoot>
  );
}
